"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Line, Bar, Pie } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"
import { getEvaluatedAssignmentAndQuiz } from "./api"
import { ChevronRight, ChevronLeft, BarChart, PieChart, LineChart, LayoutGrid, Table2, AlertCircle } from "lucide-react"

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend)

const StudentPerformance = () => {
  const [data, setData] = useState({ assignments: [], quizzes: [], overallStudentRemarks: {} })
  const [view, setView] = useState("all")
  const [chartType, setChartType] = useState("line")
  const [displayType, setDisplayType] = useState("card")
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [assignmentPage, setAssignmentPage] = useState(0)
  const [quizPage, setQuizPage] = useState(0)
  const [selectedClass, setSelectedClass] = useState("all")
  const navigate = useNavigate()
  const studentId = localStorage.getItem("studentId")
  const studentData = JSON.parse(localStorage.getItem("studentData") || "{}")

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const response = await getEvaluatedAssignmentAndQuiz(studentId)
        setData(JSON.parse(response.body).data)
      } catch (error) {
        console.error("Error fetching data:", error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [studentId])

  const convertToPercentage = (obtained, max) => (obtained / max) * 100

  const getClassInfo = (classRoomId) => {
    const classInfo = studentData.enrolledClasses?.find((c) => c.classRoomId === classRoomId)
    if (classInfo) {
      return {
        classValue: classInfo.classValue,
        subject: classInfo.subject.slice(0, 3).toUpperCase(),
      }
    }
    return { classValue: "N/A", subject: "N/A" }
  }

  const filterDataByClass = (items) => {
    if (selectedClass === "all") return items
    return items.filter((item) => item.classRoomId === selectedClass)
  }

  const getChartData = (type) => {
    const items = type === "all" ? [...data.assignments, ...data.quizzes] : data[type]
    const filteredItems = filterDataByClass(items)
    return {
      labels: filteredItems.map(
        (item) => item.assignmentTitle || item.assignment_topic || item.quizTitle || item.quiz_topic,
      ),
      datasets: [
        {
          label: "Performance",
          data: filteredItems.map((item) => convertToPercentage(item.obtainedMarks, item.maxMarks)),
          borderColor: "rgb(75, 192, 192)",
          backgroundColor: "rgba(75, 192, 192, 0.2)",
          tension: 0.1,
        },
      ],
    }
  }

  const getBarChartData = (type) => {
    const items = type === "all" ? [...data.assignments, ...data.quizzes] : data[type]
    const filteredItems = filterDataByClass(items)
    return {
      labels: filteredItems.map(
        (item) => item.assignmentTitle || item.assignment_topic || item.quizTitle || item.quiz_topic,
      ),
      datasets: [
        {
          label: "Performance",
          data: filteredItems.map((item) => convertToPercentage(item.obtainedMarks, item.maxMarks)),
          backgroundColor: "rgba(75, 192, 192, 0.6)",
          borderColor: "rgba(75, 192, 192, 1)",
          borderWidth: 1,
        },
      ],
    }
  }

  const getPieChartData = (type) => {
    const items = type === "all" ? [...data.assignments, ...data.quizzes] : data[type]
    const filteredItems = filterDataByClass(items)
    return {
      labels: filteredItems.map(
        (item) => item.assignmentTitle || item.assignment_topic || item.quizTitle || item.quiz_topic,
      ),
      datasets: [
        {
          data: filteredItems.map((item) => convertToPercentage(item.obtainedMarks, item.maxMarks)),
          backgroundColor: [
            "rgba(255, 99, 132, 0.8)",
            "rgba(54, 162, 235, 0.8)",
            "rgba(255, 206, 86, 0.8)",
            "rgba(75, 192, 192, 0.8)",
            "rgba(153, 102, 255, 0.8)",
            "rgba(255, 159, 64, 0.8)",
          ],
        },
      ],
    }
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: { display: true, text: "Your Performance", font: { size: 16 } },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: { display: true, text: "Performance (%)", font: { size: 12 } },
      },
    },
  }

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
        labels: {
          boxWidth: 15,
          font: { size: 10 },
        },
      },
      title: {
        display: true,
        text:
          view === "all"
            ? "Performance in Assignments and Quizzes"
            : `Performance in ${view.charAt(0).toUpperCase() + view.slice(1)}`,
        font: { size: 16 },
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.label}: ${context.parsed.toFixed(2)}%`,
        },
      },
    },
  }

  const renderChart = () => {
    switch (chartType) {
      case "line":
        return <Line options={chartOptions} data={getChartData(view)} />
      case "bar":
        return <Bar options={chartOptions} data={getBarChartData(view)} />
      case "pie":
        return <Pie options={pieChartOptions} data={getPieChartData(view)} />
      default:
        return null
    }
  }

  const renderItems = (items, type, currentView) => {
    let currentPage, setCurrentPage
    if (currentView === "all") {
      currentPage = type === "assignment" ? assignmentPage : quizPage
      setCurrentPage = type === "assignment" ? setAssignmentPage : setQuizPage
    } else {
      currentPage = page
      setCurrentPage = setPage
    }

    const filteredItems = filterDataByClass(items)
    const startIndex = currentPage * 4
    const endIndex = startIndex + 4
    const displayedItems = filteredItems.slice(startIndex, endIndex)

    if (displayedItems.length === 0) {
      return <p className="text-gray-600 text-center">No {type}s submitted yet.</p>
    }

    if (displayType === "card") {
      return (
        <ul className="space-y-4">
          {displayedItems.map((item, index) => {
            const { classValue, subject } = getClassInfo(item.classRoomId)
            return (
              <li
                key={index}
                className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer"
                onClick={() =>
                  navigate(type === "assignment" ? `/submitted-assignment/${item._id}` : `/quiz/results/${item._id}`)
                }
              >
                <h3 className="font-semibold text-lg text-indigo-600">
                  {item.assignmentTitle || item.assignment_topic || item.quizTitle || item.quiz_topic}
                </h3>
                <p className="text-gray-600">
                  Score: {item.obtainedMarks}/{item.maxMarks}
                </p>
                <p className="text-sm text-gray-500">
                  Due: {new Date(item.assignmentDueDate || item.quizDueDate).toLocaleDateString()}
                </p>
                <p className="text-sm text-gray-500">
                  Class: {classValue}-{subject}
                </p>
              </li>
            )
          })}
        </ul>
      )
    } else {
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-4 py-2 text-left">Title</th>
                <th className="px-4 py-2 text-left">Score</th>
                <th className="px-4 py-2 text-left">Class</th>
                <th className="px-4 py-2 text-left">Action</th>
              </tr>
            </thead>
            <tbody>
              {displayedItems.map((item, index) => {
                const { classValue, subject } = getClassInfo(item.classRoomId)
                return (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-2">
                      {item.assignmentTitle || item.assignment_topic || item.quizTitle || item.quiz_topic}
                    </td>
                    <td className="px-4 py-2">
                      {item.obtainedMarks}/{item.maxMarks}
                    </td>
                    <td className="px-4 py-2">{`${classValue}-${subject}`}</td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() =>
                          navigate(
                            type === "assignment" ? `/submitted-assignment/${item._id}` : `/quiz/results/${item._id}`,
                          )
                        }
                        className="text-indigo-600 hover:text-indigo-800"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )
    }
  }
  const renderOverallRemarks = () => {
    if (!data.overallStudentRemarks || !data.overallStudentRemarks.remarks) return null;
  
    const formatRemark = (remark) => {
      return remark
        .replace(/###\s(.*)/g, '<h3 class="text-xl font-bold text-indigo-700 mt-4 mb-2">$1</h3>')
        .replace(/\*\*(.*?)\*\*/g, '<span class="font-semibold">$1</span>')
        .replace(/\n\n/g, '</p><p class="mb-4">') // Ensures proper separation of paragraphs
        .replace(/\n/g, '<br />');
    };
  
    return (
      <div className="bg-white p-6 rounded-lg shadow-md mt-8">
        <h2 className="text-2xl font-semibold text-indigo-700 mb-4 flex items-center">
          <AlertCircle className="mr-2" size={24} />
          Overall Remarks
        </h2>
        <div className="prose max-w-none">
          
          <div
            className="text-gray-700"
            dangerouslySetInnerHTML={{ __html: `<p class="mb-2">${formatRemark(data.overallStudentRemarks.remarks)}</p>` }}
          />
          <div className="mt-4 text-sm text-gray-500">
            <p>Based on {data.overallStudentRemarks.currentCount} evaluated assignments</p>
            <p>Last updated: {new Date(data.overallStudentRemarks.updatedAt).toLocaleString()}</p>
          </div>
        </div>
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-blue-100 to-indigo-200">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 to-indigo-200 p-4 sm:p-8">
      <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-2xl overflow-hidden">
        <div className="p-4 sm:p-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-center text-indigo-700 mb-6">
            Your Performance Dashboard
          </h1>
          <div className="flex flex-wrap justify-center gap-4 mb-6">
            {["all", "assignments", "quizzes"].map((viewType) => (
              <button
                key={viewType}
                onClick={() => {
                  setView(viewType)
                  setPage(0)
                  setAssignmentPage(0)
                  setQuizPage(0)
                }}
                className={`px-4 py-2 rounded-full text-sm ${
                  view === viewType ? "bg-indigo-600 text-white" : "bg-gray-200 text-gray-700"
                } transition duration-300`}
              >
                {viewType.charAt(0).toUpperCase() + viewType.slice(1)}
              </button>
            ))}
          </div>
          <div className="mb-6">
            <div className="bg-white p-4 rounded-lg shadow-md" style={{ height: "400px" }}>
              {renderChart()}
            </div>
            <div className="mt-4 flex justify-center">
              <div className="inline-flex bg-gray-100 rounded-lg p-1">
                {[
                  { type: "line", icon: LineChart },
                  { type: "bar", icon: BarChart },
                  { type: "pie", icon: PieChart },
                ].map(({ type, icon: Icon }) => (
                  <button
                    key={type}
                    onClick={() => setChartType(type)}
                    className={`flex items-center justify-center space-x-2 px-3 py-2 rounded ${
                      chartType === type ? "bg-indigo-600 text-white" : "text-gray-700 hover:bg-gray-200"
                    } transition duration-300`}
                  >
                    <Icon size={18} />
                    <span className="hidden sm:inline">{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="bg-indigo-50 p-4 sm:p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-indigo-700">
                {view === "all" ? "Your Submissions" : view.charAt(0).toUpperCase() + view.slice(1)}
              </h2>
              <div className="flex items-center space-x-4">
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  className="p-2 border border-gray-300 rounded-md"
                >
                  <option value="all">All Classes</option>
                  {studentData.enrolledClasses?.map((classInfo) => (
                    <option key={classInfo.classRoomId} value={classInfo.classRoomId}>
                      {`${classInfo.classValue}-${classInfo.subject.slice(0, 3).toUpperCase()}`}
                    </option>
                  ))}
                </select>
                <div className="inline-flex bg-gray-100 rounded-lg p-1">
                  {[
                    { type: "card", icon: LayoutGrid },
                    { type: "table", icon: Table2 },
                  ].map(({ type, icon: Icon }) => (
                    <button
                      key={type}
                      onClick={() => setDisplayType(type)}
                      className={`flex items-center justify-center space-x-2 px-3 py-2 rounded ${
                        displayType === type ? "bg-indigo-600 text-white" : "text-gray-700 hover:bg-gray-200"
                      } transition duration-300`}
                    >
                      <Icon size={18} />
                      <span className="hidden sm:inline">{type.charAt(0).toUpperCase() + type.slice(1)} View</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {view === "all" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold text-indigo-600 mb-4">Assignments</h3>
                  {renderItems(data.assignments, "assignment", "all")}
                  <div className="flex justify-between mt-4">
                    <button
                      onClick={() => setAssignmentPage((prev) => Math.max(0, prev - 1))}
                      disabled={assignmentPage === 0}
                      className="p-2 rounded-full bg-indigo-100 text-indigo-600 disabled:opacity-50"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={() => setAssignmentPage((prev) => prev + 1)}
                      disabled={(assignmentPage + 1) * 4 >= filterDataByClass(data.assignments).length}
                      className="p-2 rounded-full bg-indigo-100 text-indigo-600 disabled:opacity-50"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-indigo-600 mb-4">Quizzes</h3>
                  {renderItems(data.quizzes, "quiz", "all")}
                  <div className="flex justify-between mt-4">
                    <button
                      onClick={() => setQuizPage((prev) => Math.max(0, prev - 1))}
                      disabled={quizPage === 0}
                      className="p-2 rounded-full bg-indigo-100 text-indigo-600 disabled:opacity-50"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={() => setQuizPage((prev) => prev + 1)}
                      disabled={(quizPage + 1) * 4 >= filterDataByClass(data.quizzes).length}
                      className="p-2 rounded-full bg-indigo-100 text-indigo-600 disabled:opacity-50"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {renderItems(data[view], view.slice(0, -1), view)}
                {filterDataByClass(data[view]).length > 4 && (
                  <div className="flex justify-between mt-4">
                    <button
                      onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                      disabled={page === 0}
                      className="p-2 rounded-full bg-indigo-100 text-indigo-600 disabled:opacity-50"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={() => setPage((prev) => prev + 1)}
                      disabled={(page + 1) * 4 >= filterDataByClass(data[view]).length}
                      className="p-2 rounded-full bg-indigo-100 text-indigo-600 disabled:opacity-50"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          {renderOverallRemarks()}
        </div>
      </div>
    </div>
  )
}

export default StudentPerformance

